import { ReservesData } from '../../finances/newReservesStore/model';
import { AmountValue, Value, AssetCategory } from 'common/interfaces';
import { DepositsData } from 'stores/workflowStores/finances/depositsStore/model';
import { TargetsData } from 'stores/workflowStores/finances/targets/model';
import { RentData } from 'stores/workflowStores/finances/rent/model';
import { PersonType, AccountMode } from 'enums';
import { ExternalProductsData, FinancialInstitution } from 'pages/Accounts/Edit/components/ExternalProducts/model';

export interface AccountEditPlan {
  fixedTax: boolean;
  personType: PersonType;
  depositData: DepositsData;
  targetData: TargetsData;
  reserveData: ReservesData;
  rentData: RentData;
  externalProductData: ExternalProductsData;
  amountAtRelevantDate: AmountValue;
  mode: AccountMode;
  portfolioOnly: boolean;
  rentApplied: boolean;
  rentApplicable: boolean;
  initial: AmountValue;
  balance: AmountValue;
  chart: Chart;
  riskProfile: number;
  buckets: Bucket[];
  risk_SHORT_TERM: number;
  risk_LONG_TERM: number;
  risk_MID_TERM: number;
  esg?: boolean;
  profileLockActive: boolean;
  profileLockDuration: number;
  options: {
    inflation: { defaultInflationRate: number; defaultInflationType: string }
    specialAssets: {
      repo: string;
      reserves: string[];
      savings: string[];
    }
  };
}

export interface CompositionData {
// FIXME: enums
    asset: { [ key: string] : number };
    region: { [ key: string] : number };
    currency: { [ key: string] : number };
    financialInstitution: { [ key: string] : number };

}

export interface Chart {
  dataset?: DatasetBE[];
  metadata: ChartMetadata;
  valueAtEnd: AmountValue;
  endDate: string;
  valueAtDisplay: AmountValue;
  displayDate: string;
  ageAtDisplay: string;
  inflation: { enabled: boolean; rate: number };
  composition: CompositionData;
}

export interface DatasetBE {
  title: string;
  data: { [key: string]: number };
  detailData?: { [key: string]: string | AmountValue };
}

export interface Dataset {
  title: string;
  data: DatasetData;
  detailData?: DatasetData;
}

export type DatasetData = DatasetDataArray[];

export type DatasetDataArray = [string, number];

export interface ChartMetadata {
  rentStartIndex: number;
  ageAxisStep: number;
  min: string;
  max: string; // dates
  partnerAgePresent?: boolean;
  financialInstitutions: { [key: string] : FinancialInstitution }
}

export interface ChartComputedData {
  inflation?: boolean;
  inflationRate?: number;
  endDate: string | null;
  endValue: number | null;
  displayDate: string | null;
  displayAge: string | null;
  displayValue: number | null;
  showRange: boolean;
  displaySetValue: boolean;
  totalIndex: number;
  ageIndex: number;
  default?: boolean;
}
export const defaultChartData = {
  default: true,
  inflation: true,
  inflationRate: 0,
  endDate: null,
  endValue: null,
  displayDate: null,
  displayAge: null,
  displayValue: null,
  displaySetValue: false,
  showRange: false,
  totalIndex: 0,
  ageIndex: 0,
};

export enum PortfolioType {
  STD = 'STD',
  ESG = 'ESG',
  BAS = 'BAS',
  CUS = 'CUS',
  IND = 'IND',
}

export interface PortfolioCode {
  code: string;
  profile: string;
  type: PortfolioType;
  title?: string;
}

export interface Portfolio {
  code: PortfolioCode;
  title: string;
  income: number;
  loss: number;
  terAverage: number;
  distributionTotal: number;
  assetTotal?: AmountValue;
  assetCategories: AssetCategory[];
  chart: {
    dataset: BucketPieDataset[];
  };
  msciRating?: number;
  carbon?: number;

  options: {
    code: PortfolioCode[];
    individualPortfolio?: {
      title: string;
      calculatedProfile: number;
    }
  };
}

export interface Bucket extends SimpleBucket {
  riskProfile: string;
  originalRiskProfile?: string;
  minProfile: string;
  maxProfile: string;
  reserved?: {
    value: AmountValue;
    horizon: string;
    options: {
      horizon: string[];
    };
  };
  portfolio: Portfolio;
  currencyHedging?: boolean;
  automaticRebalancing?: boolean;
  rebalancingTimeTest: boolean;
  oneTimeRebalance: boolean;
  accountDetailData: AccountDetailBucketData;
  drift?: number;
  options: {
    driftThreshold: [number, number]
  }
}

export enum BucketType {
  LONG_TERM = 'LONG_TERM',
  MID_TERM = 'MID_TERM',
  SHORT_TERM = 'SHORT_TERM',
  TOTAL = 'TOTAL',
}

export interface SimpleBucket {
  type: BucketType;
  active: boolean;
  balance: AmountValue;
  title: string;
  subtitle: string;
}

export interface BucketPieDataset {
  share: number;
  code: string;
  title: string;
}

//export interface CompositionBucketPieDataset { [id in ExtProductAsset]?: number }

interface AccountDetailBucketData {
  currentValue: Value;
  currentRevenue: Value;
  updatable: boolean;
}
